import { useEffect, useRef, useState } from "react";
import Select, { SingleValue } from 'react-select';
import { CardVariant } from "../../../models/CardVariant";
import { CardImage } from "../../CardVariantImage/CardImage";
import { CrystalWithCost } from "../../Crystal/CrystalWithCost";
import { classNames } from "../../../style/common";
import { ComboBoxOption } from "../../../models/Input";
import { CardFilterState, filterCards } from "../../../functions/filter/CardFilterCommands";

export type CardComboBoxProps<
    IsMulti extends boolean
> = {
    variants: CardVariant[],
    selection: CardVariant | null,
    isMulti: IsMulti,
    isDisabled?: boolean,
    filterState: CardFilterState,
    placeholder?: string | undefined,
    closeMenuOnSelect?: boolean | undefined,
    onSelectionChange: (newSelection: CardVariant | null) => void,
    onEnterPress?: (searchText: string) => void
}

export default function CardComboBox<
    IsMulti extends boolean
>(
    props: CardComboBoxProps<IsMulti>
) {
    const MAX_DISPLAY_SIZE = 48;
    const [displayedOptions, setDisplayedOptions] = useState<ComboBoxOption<CardVariant>[]>([]);
    const [selection, setSelection] = useState<SingleValue<ComboBoxOption<CardVariant>>>(null);
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [text, setText] = useState<string>("");

    const filter = (searchText: string) => {
        const newFilterState = JSON.parse(JSON.stringify(props.filterState)) as CardFilterState;
        newFilterState.searchText = searchText;

        const matchingCards = filterCards(props.variants, newFilterState, {});
        const newDisplayedOptions = matchingCards.slice(0, MAX_DISPLAY_SIZE).map((item) => ({
            value: item,
            label: item.serial
        }));

        setDisplayedOptions(newDisplayedOptions);
    }

    useEffect(() => {
        filter(text);
    }, [props.variants]);

    useEffect(() => {
        setSelection(props.selection ? getSelectionFromVariant(props.selection) : null);
    }, [props.selection]);

    return (
        <Select
            menuPortalTarget={document.body}
            isDisabled={props.isDisabled}
            value={selection}
            blurInputOnSelect={true}
            inputValue={text}
            classNames={{
                control: (internalProps) => classNames(
                    props.isDisabled ? "!bg-muted !text-contrast-tertiary" : "!bg-secondary !text-contrast-primary !border-color-border/75"
                ),
                placeholder: () => `!text-contrast-tertiary`,
                input: () => `!text-contrast-primary !outline-none`,
                loadingIndicator: () => `!text-white`,
                menuList: () => `!text-contrast-primary`,
                menu: () => `!z-50 min-w-[300px] !bg-primary !border !border-color-border`,
                option: () => "hover:!bg-secondary",
                menuPortal: () => `!z-[500]`
            }}
            styles={{
                input: (base) => ({
                    ...base,
                    'input:focus': {
                        boxShadow: 'none'
                    }
                })
            }}
            onKeyDown={(event) => {
                if (!props.onEnterPress) {
                    return;
                }

                if (event.key === "Enter") {
                    event.stopPropagation();
                    event.preventDefault();

                    filter("");
                    setIsLoading(false);
                    setIsOpen(false);
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }

                    props.onEnterPress(text);
                }
            }}
            isClearable={true}
            onMenuClose={() => setIsOpen(false)}
            onMenuOpen={() => setIsOpen(true)}
            onInputChange={(newText, inputAction) => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                if (inputAction.action === "input-blur" || inputAction.action === "menu-close") {
                    setText("");
                    filter("");
                    setIsLoading(false);
                    setIsOpen(false);
                    return;
                }

                setText(newText);
                setIsLoading(true);

                if (!newText) {
                    filter("");
                    setIsOpen(false);
                    setIsLoading(false);
                    return;
                } else if (newText && !isOpen) {
                    setIsOpen(true);
                }

                timeoutRef.current = setTimeout(() => {
                    filter(newText);
                    setIsLoading(false);
                }, 500);
            }}
            components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
                SingleValue: (singleValueProps) => {
                    return (
                        <div className="absolute px-1 py-2 select-none flex items-baseline">
                            <div className="pl-2 mr-2 font-semibold text-sm shrink-0">{singleValueProps.data.value.serial}</div>
                            <div className="text-ellipsis whitespace-nowrap overflow-hidden">{singleValueProps.data.value.card.name}</div>
                        </div>
                    )
                },
                Option: (optionProps) => {
                    if (!optionProps?.data) {
                        return <></>;
                    }

                    return (
                        <div className="px-1 py-2 select-none hover:bg-secondary flex items-center content-center" onClick={() => {
                            setSelection(optionProps.data);
                            props.onSelectionChange(optionProps.data.value);

                            if (props.closeMenuOnSelect) {
                                setText("");
                                setIsOpen(false);
                            }
                        }}>
                            <CrystalWithCost
                                cost={optionProps.data.value.card.cost ?? 0}
                                elements={optionProps.data.value.card.elements ?? []}
                                classNames={{
                                    crystal: "h-10"
                                }} />

                            <CardImage variant={optionProps.data!.value} className="h-10 mr-6 ml-1" />
                            <div className="flex items-baseline">
                                <div className="w-[75px] min-w-[75px] font-semibold text-gray-500 text-sm">{optionProps.data.value.serial}</div>
                                <div>{optionProps.data.value.card.name}</div>
                            </div>
                        </div>
                    )
                }
            }}
            filterOption={() => true}
            menuIsOpen={isOpen}
            onChange={(option, action) => {
                setSelection(option);
                props.onSelectionChange(option ? option.value : null);

                if (action.action === "clear") {
                    setDisplayedOptions([]);
                    setText("");
                    setIsOpen(false);
                }

                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            }}
            placeholder={props.placeholder}
            options={displayedOptions}
            isLoading={isLoading} />
    )
}

function getSelectionFromVariant(variant: CardVariant): SingleValue<ComboBoxOption<CardVariant>> {
    return {
        value: variant,
        label: variant.serial
    }
}